import React from "react";
import {
  ISchoolStudentApplicationCardFrontEndModel,
  ISchoolStudentApplicationCardHeaderContent,
  ISchoolStudentApplicationCardLanguageCourseBackEndModel,
  IStudentApplicationCardCenterContent,
} from "../types";
import { beautifyRequiredDocuments } from "../../program/helpers/helperFunctions";
import moment from "moment";
import { beautifyDurationUnit } from "../../../helpers";
import { EducationType } from "../../school/constants/types";
import {
  calcFeeWithDiscount,
  sumPriceAndOtherFeesSupports,
} from "../../language-course/helpers/helperFunctions";
import { LANGUAGES } from "../../language-course/constants";
import { PROGRAM_TYPES, ProgramType } from "../../program/constants";
import { ColorName } from "../../../types";
import { ReactComponent as SubmissionDate } from "../../../assets/svg/hourglass.svg";
import { CardFieldType } from "../../program/components/constants";
import { ReactComponent as RequiredDocumentsInsideCircle } from "../../../assets/svg/required-documents-inside-circle.svg";
import { ExpandedRequiredDocuments } from "../../program/components/CardField/components/ExpandedRequiredDocuments";
import { ReactComponent as Branch } from "../../../assets/svg/branch-location.svg";
import { ReactComponent as ProgramDuration } from "../../../assets/svg/duration.svg";

function parser(
  application: ISchoolStudentApplicationCardLanguageCourseBackEndModel,
) {
  const capitalizedRequiredDocuments = beautifyRequiredDocuments(
    application.course.requiredDocs,
  );

  const { hasSupport: hasPriceSupport, calcFee: calcPrice } =
    calcFeeWithDiscount({
      fee: application.course.price,
      hasFeeDiscount: application.course.hasPriceDiscount,
      feeDiscountType: application.course.priceDiscountType,
      feeDiscountAmount: application.course.priceDiscountAmount,
      currency: application.course.currency,
    });

  const { hasSupport: hasOtherFeesSupport, calcFee: calcOtherFees } =
    calcFeeWithDiscount({
      fee: application.course.otherFeesTotal,
      hasFeeDiscount: application.course.hasOtherFeesDiscount,
      feeDiscountType: application.course.otherFeesDiscountType,
      feeDiscountAmount: application.course.otherFeesDiscountAmount,
      currency: application.course.currency,
    });

  const {
    priceOtherFeesSumWithCurrency,
    priceOtherFeesWithSupportsSumWithCurrency,
  } = sumPriceAndOtherFeesSupports(
    application.course.price,
    hasPriceSupport,
    calcPrice,
    application.course.otherFeesTotal,
    hasOtherFeesSupport,
    calcOtherFees,
    application.course.currency,
  );

  const courseDuration = `${
    application.course.durationAmount
  } ${beautifyDurationUnit(
    application.course.durationAmount,
    application.course.durationUnit,
    "Course",
  )}`;

  const priceDuration = `${
    application.course.priceDurationAmount == null
      ? ""
      : application.course.priceDurationAmount + " "
  }
        ${beautifyDurationUnit(
          application.course.priceDurationAmount,
          application.course.priceDurationUnit,
          "Course",
        )}`;

  const totalPriceWithUnit =
    priceOtherFeesWithSupportsSumWithCurrency + " / " + priceDuration;

  const schoolCancellationNote = !application.schoolCancellationNote
    ? null
    : {
        ...application.schoolCancellationNote,
        date: moment(application.schoolCancellationNote.created_at).format(
          "ddd, MMM D, YYYY, h:mm A",
        ),
      };

  const studentCancellationNote = !application.studentCancellationNote
    ? null
    : {
        ...application.studentCancellationNote,
        date: moment(application.studentCancellationNote.created_at).format(
          "ddd, MMM D, YYYY, h:mm A",
        ),
      };

  const headerContent: ISchoolStudentApplicationCardHeaderContent = {
    leftSection: {
      lRow0: {
        text0: LANGUAGES.find((l) => l.id === application.course.language)!
          .name,
      },
      lRow2: {
        text0: "Category - ",
        text1: application.course.category,
      },
    },
    rightSection: {
      rRow0: {
        text0: PROGRAM_TYPES.find(
          (type) => type.id === application.course.courseType,
        )!.name,
        text0BackgroundColor:
          application.course.courseType === +ProgramType.ONLINE
            ? ColorName.ONLINE
            : ColorName.ON_CAMPUS,
      },
      rRow1: {
        text0: totalPriceWithUnit,
        text1: priceOtherFeesSumWithCurrency,
      },
    },
  };

  const centerContent: IStudentApplicationCardCenterContent[] = [
    {
      id: CardFieldType.SubmissionDate,
      icon: SubmissionDate,
      title: "Submission Date",
      previewContent: [application.created_at],
    },
    {
      id: CardFieldType.RequiredDocuments,
      icon: RequiredDocumentsInsideCircle,
      title: "Required Documents",
      previewContent: capitalizedRequiredDocuments,
      expandedContent: () => (
        <ExpandedRequiredDocuments reqDocs={capitalizedRequiredDocuments} />
      ),
    },
    {
      id: CardFieldType.Branch,
      icon: Branch,
      title: "Branch",
      previewContent: [application.branch.name],
    },
    {
      id: CardFieldType.ProgramDuration,
      icon: ProgramDuration,
      title: "Program Duration",
      previewContent: [courseDuration],
    },
  ];

  const parsedApplication: ISchoolStudentApplicationCardFrontEndModel = {
    headerContent,
    centerContent,
    schoolType: EducationType.Language,
    schoolCancellationNote,
    studentCancellationNote,
    id: application.id,
    status: application.status,
    submissionDate: application.created_at,
    entityId: application.course.id,
    entityName: "course",
  };

  return parsedApplication;
}

export function parseToSchoolStudentCardsLanguageCourseFrontEndModel(response) {
  response.data = response.data?.map((program) => parser(program));

  return response;
}
